import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { PlusIcon, BoltIcon } from '@heroicons/react/24/outline';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import Select from '@/components/common/Select';
import SearchInput from '@/components/common/SearchInput';
import { formatDate, formatCurrency } from '@/utils/formatters';
import { useAuthStore } from '@/stores/authStore';
import {
  getPromoCodes,
  getPromoCodeDetail,
  createPromoCode,
  bulkCreatePromoCodes,
  updatePromoCode,
  deactivatePromoCode,
  reactivatePromoCode,
  getPromoCodeStats,
  getPromoCodeCategories,
  getPlans,
} from '@/services/promoCodeApi';
import type { PromoCode, PromoCodeCategory, PromoCodeStatus } from '@/types/promoCode';
import toast from 'react-hot-toast';

function getCodeStatus(code: PromoCode): PromoCodeStatus {
  if (!code.is_active) return 'inactive';
  if (code.expires_at && new Date(code.expires_at) < new Date()) return 'expired';
  return 'active';
}

function formatDiscount(code: PromoCode): string {
  if (code.discount_type === 'percent') return `${code.percent_off}% off`;
  return `${formatCurrency(code.amount_off_cents)} off`;
}

function formatDuration(code: PromoCode): string {
  if (code.duration === 'once') return 'One-time';
  if (code.duration === 'forever') return 'Forever';
  return `${code.duration_in_months} months`;
}

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
];

const discountTypeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'percent', label: 'Percentage' },
  { value: 'amount', label: 'Dollar Amount' },
];

const durationOptions = [
  { value: 'once', label: 'One-time' },
  { value: 'repeating', label: 'Repeating' },
  { value: 'forever', label: 'Forever' },
];

const billingCycleOptions = [
  { value: 'both', label: 'Both' },
  { value: 'monthly', label: 'Monthly Only' },
  { value: 'yearly', label: 'Yearly Only' },
];

const initialCreateForm = {
  code: '',
  discountType: 'percent' as const,
  percentOff: '',
  amountOff: '',
  duration: 'once' as const,
  durationInMonths: '',
  restrictedPlanIds: [] as string[],
  applicableBillingCycles: 'both' as 'both' | 'monthly' | 'yearly',
  maxRedemptions: '',
  expiresAt: '',
  categoryId: '',
  internalName: '',
  notes: '',
};

const initialBulkForm = {
  prefix: '',
  quantity: '5',
  codeFormat: 'sequential' as 'sequential' | 'random',
  discountType: 'percent' as const,
  percentOff: '',
  amountOff: '',
  duration: 'once' as const,
  durationInMonths: '',
  restrictedPlanIds: [] as string[],
  applicableBillingCycles: 'both' as 'both' | 'monthly' | 'yearly',
  maxRedemptions: '',
  expiresAt: '',
  categoryId: '',
  internalName: '',
  notes: '',
};

export default function PromoCodes() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('promo_codes' as any, 'write');

  // Filters
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [discountTypeFilter, setDiscountTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedCodeId, setSelectedCodeId] = useState<string | null>(null);

  // Forms
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [bulkForm, setBulkForm] = useState(initialBulkForm);

  // Detail editing
  const [editNotes, setEditNotes] = useState('');
  const [editInternalName, setEditInternalName] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editMaxRedemptions, setEditMaxRedemptions] = useState('');

  // Queries
  const { data: stats } = useQuery({
    queryKey: ['promoCodeStats'],
    queryFn: getPromoCodeStats,
  });

  const { data: listData, isLoading } = useQuery({
    queryKey: ['promoCodes', page, statusFilter, categoryFilter, discountTypeFilter, searchQuery],
    queryFn: () => getPromoCodes(page, 20, statusFilter, categoryFilter !== 'all' ? categoryFilter : undefined, discountTypeFilter, searchQuery || undefined),
  });

  const { data: categories } = useQuery({
    queryKey: ['promoCodeCategories'],
    queryFn: getPromoCodeCategories,
  });

  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: getPlans,
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['promoCodeDetail', selectedCodeId],
    queryFn: () => getPromoCodeDetail(selectedCodeId!),
    enabled: !!selectedCodeId,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: () => {
      const payload: any = {
        code: createForm.code,
        discountType: createForm.discountType,
        duration: createForm.duration,
      };
      if (createForm.discountType === 'percent') payload.percentOff = parseFloat(createForm.percentOff);
      if (createForm.discountType === 'amount') payload.amountOffCents = Math.round(parseFloat(createForm.amountOff) * 100);
      if (createForm.duration === 'repeating') payload.durationInMonths = parseInt(createForm.durationInMonths);
      if (createForm.restrictedPlanIds.length) payload.restrictedPlanIds = createForm.restrictedPlanIds;
      if (createForm.applicableBillingCycles !== 'both') payload.applicableBillingCycles = createForm.applicableBillingCycles;
      if (createForm.maxRedemptions) payload.maxRedemptions = parseInt(createForm.maxRedemptions);
      if (createForm.expiresAt) payload.expiresAt = createForm.expiresAt;
      if (createForm.categoryId) payload.categoryId = createForm.categoryId;
      if (createForm.internalName) payload.internalName = createForm.internalName;
      if (createForm.notes) payload.notes = createForm.notes;
      return createPromoCode(payload);
    },
    onSuccess: () => {
      toast.success('Promo code created');
      queryClient.invalidateQueries({ queryKey: ['promoCodes'] });
      queryClient.invalidateQueries({ queryKey: ['promoCodeStats'] });
      setShowCreateModal(false);
      setCreateForm(initialCreateForm);
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to create promo code'),
  });

  const bulkMutation = useMutation({
    mutationFn: () => {
      const payload: any = {
        prefix: bulkForm.prefix,
        quantity: parseInt(bulkForm.quantity),
        codeFormat: bulkForm.codeFormat,
        discountType: bulkForm.discountType,
        duration: bulkForm.duration,
      };
      if (bulkForm.discountType === 'percent') payload.percentOff = parseFloat(bulkForm.percentOff);
      if (bulkForm.discountType === 'amount') payload.amountOffCents = Math.round(parseFloat(bulkForm.amountOff) * 100);
      if (bulkForm.duration === 'repeating') payload.durationInMonths = parseInt(bulkForm.durationInMonths);
      if (bulkForm.restrictedPlanIds.length) payload.restrictedPlanIds = bulkForm.restrictedPlanIds;
      if (bulkForm.applicableBillingCycles !== 'both') payload.applicableBillingCycles = bulkForm.applicableBillingCycles;
      if (bulkForm.maxRedemptions) payload.maxRedemptions = parseInt(bulkForm.maxRedemptions);
      if (bulkForm.expiresAt) payload.expiresAt = bulkForm.expiresAt;
      if (bulkForm.categoryId) payload.categoryId = bulkForm.categoryId;
      if (bulkForm.internalName) payload.internalName = bulkForm.internalName;
      if (bulkForm.notes) payload.notes = bulkForm.notes;
      return bulkCreatePromoCodes(payload);
    },
    onSuccess: (data) => {
      toast.success(`${data.count} promo codes generated`);
      queryClient.invalidateQueries({ queryKey: ['promoCodes'] });
      queryClient.invalidateQueries({ queryKey: ['promoCodeStats'] });
      setShowBulkModal(false);
      setBulkForm(initialBulkForm);
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to generate codes'),
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivatePromoCode,
    onSuccess: () => {
      toast.success('Promo code deactivated');
      queryClient.invalidateQueries({ queryKey: ['promoCodes'] });
      queryClient.invalidateQueries({ queryKey: ['promoCodeStats'] });
      queryClient.invalidateQueries({ queryKey: ['promoCodeDetail'] });
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to deactivate'),
  });

  const reactivateMutation = useMutation({
    mutationFn: reactivatePromoCode,
    onSuccess: () => {
      toast.success('Promo code reactivated');
      queryClient.invalidateQueries({ queryKey: ['promoCodes'] });
      queryClient.invalidateQueries({ queryKey: ['promoCodeStats'] });
      queryClient.invalidateQueries({ queryKey: ['promoCodeDetail'] });
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to reactivate'),
  });

  const updateMutation = useMutation({
    mutationFn: () => updatePromoCode(selectedCodeId!, {
      categoryId: editCategoryId || undefined,
      internalName: editInternalName || undefined,
      notes: editNotes || undefined,
      maxRedemptions: editMaxRedemptions,
    }),
    onSuccess: (res: any) => {
      toast.success('Promo code updated');
      if (res?.stripeWarning) {
        toast(`Stripe warning: ${res.stripeWarning}. Max redemptions saved in system only.`, { duration: 8000 });
      }
      queryClient.invalidateQueries({ queryKey: ['promoCodes'] });
      queryClient.invalidateQueries({ queryKey: ['promoCodeDetail', selectedCodeId] });
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to update'),
  });

  // Category options for Select
  const categoryOptions = useMemo(() => [
    { value: 'all', label: 'All Categories' },
    ...(categories || []).map((c) => ({ value: c.id, label: c.name })),
  ], [categories]);

  const categoryFormOptions = useMemo(() => [
    { value: '', label: 'No Category' },
    ...(categories || []).map((c) => ({ value: c.id, label: c.name })),
  ], [categories]);

  // Open detail modal
  const openDetail = (code: PromoCode) => {
    setSelectedCodeId(code.id);
    setEditNotes(code.notes || '');
    setEditInternalName(code.internal_name || '');
    setEditCategoryId(code.category_id || '');
    setEditMaxRedemptions(code.max_redemptions != null ? String(code.max_redemptions) : '');
  };

  // Discount config form fields (shared between create and bulk modals)
  const renderDiscountFields = (form: typeof createForm, setForm: (f: any) => void, fieldPrefix: string) => {
    const discountTypeName = `${fieldPrefix}-discount-type`;
    const percentOffId = `${fieldPrefix}-percent-off`;
    const amountOffId = `${fieldPrefix}-amount-off`;
    const durationMonthsId = `${fieldPrefix}-duration-months`;
    const maxRedemptionsId = `${fieldPrefix}-max-redemptions`;
    const expiresAtId = `${fieldPrefix}-expires-at`;
    const internalNameId = `${fieldPrefix}-internal-name`;
    const notesId = `${fieldPrefix}-notes`;
    return (
      <div className="space-y-4">
      {/* Discount Type */}
      <div>
        <p className="block text-sm font-medium text-gray-700 mb-2">Discount Type</p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={discountTypeName}
              checked={form.discountType === 'percent'}
              onChange={() => setForm({ ...form, discountType: 'percent' })}
              className="text-brand"
              aria-label="Percentage discount type"
            />
            <span className="text-sm">Percentage</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={discountTypeName}
              checked={form.discountType === 'amount'}
              onChange={() => setForm({ ...form, discountType: 'amount' })}
              className="text-brand"
              aria-label="Dollar amount discount type"
            />
            <span className="text-sm">Dollar Amount</span>
          </label>
        </div>
      </div>

      {/* Discount Value */}
      {form.discountType === 'percent' ? (
        <div>
          <label htmlFor={percentOffId} className="block text-sm font-medium text-gray-700 mb-1">Percent Off</label>
          <div className="relative">
            <input
              id={percentOffId}
              type="number"
              min="1"
              max="100"
              value={form.percentOff}
              onChange={(e) => setForm({ ...form, percentOff: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="25"
              aria-label="Percent off"
            />
            <span className="absolute right-3 top-2 text-gray-400">%</span>
          </div>
        </div>
      ) : (
        <div>
          <label htmlFor={amountOffId} className="block text-sm font-medium text-gray-700 mb-1">Amount Off</label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-400">$</span>
            <input
              id={amountOffId}
              type="number"
              min="0.01"
              step="0.01"
              value={form.amountOff}
              onChange={(e) => setForm({ ...form, amountOff: e.target.value })}
              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="10.00"
              aria-label="Amount off"
            />
          </div>
        </div>
      )}

      {/* Duration */}
      <div>
        <p className="block text-sm font-medium text-gray-700 mb-1">Duration</p>
        <Select
          value={form.duration}
          onChange={(val) => setForm({ ...form, duration: val })}
          options={durationOptions}
          aria-label="Promo code duration"
        />
      </div>

      {form.duration === 'repeating' && (
        <div>
          <label htmlFor={durationMonthsId} className="block text-sm font-medium text-gray-700 mb-1">Duration (months)</label>
          <input
            id={durationMonthsId}
            type="number"
            min="1"
            max="36"
            value={form.durationInMonths}
            onChange={(e) => setForm({ ...form, durationInMonths: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            placeholder="3"
            aria-label="Duration in months"
          />
        </div>
      )}

      {/* Plan Restrictions */}
      {plans && plans.length > 0 && (
        <div>
          <p className="block text-sm font-medium text-gray-700 mb-2">Restrict to Plans</p>
          <div className="space-y-2">
            {plans.map((plan) => (
              <label key={plan.plan_id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.restrictedPlanIds.includes(plan.plan_id)}
                  name={`${fieldPrefix}-restricted-plan`}
                  onChange={(e) => {
                    const ids = e.target.checked
                      ? [...form.restrictedPlanIds, plan.plan_id]
                      : form.restrictedPlanIds.filter((id) => id !== plan.plan_id);
                    setForm({ ...form, restrictedPlanIds: ids });
                  }}
                  className="rounded text-brand"
                  aria-label={`Restrict to plan ${plan.name}`}
                />
                <span className="text-sm">{plan.name}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">Leave unchecked for all plans</p>
        </div>
      )}

      {/* Billing Cycle Restriction */}
      <div>
        <p className="block text-sm font-medium text-gray-700 mb-2">Applicable Billing Cycles</p>
        <div className="flex gap-4">
          {billingCycleOptions.map((opt) => (
            <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name={`${fieldPrefix}-billing-cycle`}
                value={opt.value}
                checked={form.applicableBillingCycles === opt.value}
                onChange={() => setForm({ ...form, applicableBillingCycles: opt.value as 'both' | 'monthly' | 'yearly' })}
                className="text-brand"
                aria-label={`Applicable billing cycle ${opt.label}`}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">Restrict which billing cycle this code can be used with</p>
      </div>

      {/* Max Redemptions */}
      <div>
        <label htmlFor={maxRedemptionsId} className="block text-sm font-medium text-gray-700 mb-1">Max Redemptions</label>
        <input
          id={maxRedemptionsId}
          type="number"
          min="1"
          value={form.maxRedemptions}
          onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          placeholder="Unlimited"
          aria-label="Max redemptions"
        />
      </div>

      {/* Expiration */}
      <div>
        <label htmlFor={expiresAtId} className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
        <input
          id={expiresAtId}
          type="datetime-local"
          value={form.expiresAt}
          onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          aria-label="Expiration date and time"
        />
      </div>

      {/* Category */}
      <div>
        <p className="block text-sm font-medium text-gray-700 mb-1">Category</p>
        <Select
          value={form.categoryId}
          onChange={(val) => setForm({ ...form, categoryId: val })}
          options={categoryFormOptions}
          aria-label="Promo code category"
        />
      </div>

      {/* Internal Name */}
      <div>
        <label htmlFor={internalNameId} className="block text-sm font-medium text-gray-700 mb-1">Internal Name</label>
        <input
          id={internalNameId}
          type="text"
          value={form.internalName}
          onChange={(e) => setForm({ ...form, internalName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          placeholder="Q1 Marketing Campaign"
          aria-label="Internal Name"
        />
      </div>

      {/* Notes */}
      <div>
        <label htmlFor={notesId} className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          id={notesId}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          placeholder="Internal notes about this promo code..."
          aria-label="Notes"
        />
      </div>
      </div>
    );
  };

  // Table columns
  const columns = useMemo<ColumnDef<PromoCode>[]>(() => [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <span className="font-mono text-sm font-medium">{row.original.code}</span>
      ),
    },
    {
      id: 'discount',
      header: 'Discount',
      cell: ({ row }) => (
        <span className="font-medium text-green-700">{formatDiscount(row.original)}</span>
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => (
        <Badge variant="info" size="sm">{formatDuration(row.original)}</Badge>
      ),
    },
    {
      id: 'plans',
      header: 'Plans',
      cell: ({ row }) => {
        if (!row.original.restricted_plan_ids || row.original.restricted_plan_ids.length === 0) {
          return <span className="text-gray-500 text-sm">All Plans</span>;
        }
        const planNames = row.original.restricted_plan_ids
          .map((id) => plans?.find((p) => p.plan_id === id)?.name)
          .filter(Boolean);
        return (
          <div className="flex flex-wrap gap-1">
            {planNames.map((name) => (
              <Badge key={name} variant="primary" size="sm">{name}</Badge>
            ))}
          </div>
        );
      },
    },
    {
      id: 'billing',
      header: 'Billing',
      cell: ({ row }) => {
        const cycle = row.original.applicable_billing_cycles;
        if (!cycle || cycle === 'both') return <span className="text-gray-500 text-sm">Both</span>;
        return <Badge variant={cycle === 'monthly' ? 'info' : 'primary'} size="sm">{cycle === 'monthly' ? 'Monthly' : 'Yearly'}</Badge>;
      },
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => {
        if (!row.original.category) return <span className="text-gray-400 text-sm">-</span>;
        return <Badge variant="gray" size="sm">{row.original.category.name}</Badge>;
      },
    },
    {
      id: 'redemptions',
      header: 'Redemptions',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.times_redeemed} / {row.original.max_redemptions || '\u221E'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = getCodeStatus(row.original);
        const config = {
          active: { variant: 'success' as const, label: 'Active' },
          inactive: { variant: 'gray' as const, label: 'Inactive' },
          expired: { variant: 'danger' as const, label: 'Expired' },
        };
        const { variant, label } = config[status];
        return <Badge variant={variant} dot>{label}</Badge>;
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => <span className="text-sm text-gray-500">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const code = row.original;
        const status = getCodeStatus(code);
        return (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); openDetail(code); }}>
              Details
            </Button>
            {canWrite && status === 'active' && (
              <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); deactivateMutation.mutate(code.id); }}>
                Deactivate
              </Button>
            )}
            {canWrite && status === 'inactive' && (
              <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); reactivateMutation.mutate(code.id); }}>
                Reactivate
              </Button>
            )}
          </div>
        );
      },
    },
  ], [canWrite, plans, deactivateMutation, reactivateMutation]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Promo Codes</h1>
        <p className="text-gray-500 mt-1">Create and manage discount codes synced with Stripe</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody padding="sm">
            <p className="text-sm text-gray-500">Total Codes</p>
            <p className="text-2xl font-bold">{stats?.totalCodes ?? '-'}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody padding="sm">
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-600">{stats?.activeCodes ?? '-'}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody padding="sm">
            <p className="text-sm text-gray-500">Total Redemptions</p>
            <p className="text-2xl font-bold">{stats?.totalRedemptions ?? '-'}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody padding="sm">
            <p className="text-sm text-gray-500">Total Savings</p>
            <p className="text-2xl font-bold text-blue-600">{stats ? formatCurrency(stats.totalSavingsCents) : '-'}</p>
          </CardBody>
        </Card>
      </div>

      {/* Filters + Actions */}
      <Card>
        <CardBody padding="sm">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center">
            <div className="flex-1 w-full lg:w-auto">
              <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search codes, names, notes..." />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={statusFilter} onChange={(val) => { setStatusFilter(val); setPage(1); }} options={statusOptions} />
              <Select value={categoryFilter} onChange={(val) => { setCategoryFilter(val); setPage(1); }} options={categoryOptions} />
              <Select value={discountTypeFilter} onChange={(val) => { setDiscountTypeFilter(val); setPage(1); }} options={discountTypeOptions} />
              {canWrite && (
                <>
                  <Button onClick={() => setShowCreateModal(true)} leftIcon={<PlusIcon className="w-4 h-4" />}>
                    Create Code
                  </Button>
                  <Button variant="secondary" onClick={() => setShowBulkModal(true)} leftIcon={<BoltIcon className="w-4 h-4" />}>
                    Bulk Generate
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Data Table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={listData?.codes || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={listData?.total || 0}
            onPageChange={setPage}
            onRowClick={(row) => openDetail(row)}
            getRowId={(row) => row.id}
          />
        </CardBody>
      </Card>

      {/* Create Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Promo Code" size="lg">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <label htmlFor="create-promo-code" className="block text-sm font-medium text-gray-700 mb-1">Code</label>
            <input
              id="create-promo-code"
              type="text"
              value={createForm.code}
              onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono"
              placeholder="SAVE20"
              maxLength={30}
              aria-label="Promo code"
            />
          </div>
          {renderDiscountFields(createForm, setCreateForm, 'create')}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending}
            disabled={!createForm.code || (createForm.discountType === 'percent' ? !createForm.percentOff : !createForm.amountOff)}>
            Create
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Generate Modal */}
      <Modal isOpen={showBulkModal} onClose={() => setShowBulkModal(false)} title="Bulk Generate Promo Codes" size="lg">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="bulk-code-prefix" className="block text-sm font-medium text-gray-700 mb-1">Prefix</label>
              <input
                id="bulk-code-prefix"
                type="text"
                value={bulkForm.prefix}
                onChange={(e) => setBulkForm({ ...bulkForm, prefix: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono"
                placeholder="SUMMER"
                maxLength={20}
                aria-label="Code prefix"
              />
            </div>
            <div>
              <label htmlFor="bulk-code-quantity" className="block text-sm font-medium text-gray-700 mb-1">Quantity (1-100)</label>
              <input
                id="bulk-code-quantity"
                type="number"
                min="1"
                max="100"
                value={bulkForm.quantity}
                onChange={(e) => setBulkForm({ ...bulkForm, quantity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label="Quantity of codes"
              />
            </div>
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">Code Format</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="bulk-code-format"
                  checked={bulkForm.codeFormat === 'sequential'}
                  onChange={() => setBulkForm({ ...bulkForm, codeFormat: 'sequential' })}
                  className="text-brand"
                  aria-label="Sequential code format"
                />
                <span className="text-sm">Sequential (PREFIX-001, PREFIX-002)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="bulk-code-format"
                  checked={bulkForm.codeFormat === 'random'}
                  onChange={() => setBulkForm({ ...bulkForm, codeFormat: 'random' })}
                  className="text-brand"
                  aria-label="Random code format"
                />
                <span className="text-sm">Random (PREFIX-X7K2A1)</span>
              </label>
            </div>
          </div>

          {/* Preview */}
          {bulkForm.prefix && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Preview:</p>
              <p className="font-mono text-sm">
                {bulkForm.codeFormat === 'sequential'
                  ? `${bulkForm.prefix}-001, ${bulkForm.prefix}-002, ...`
                  : `${bulkForm.prefix}-X7K2, ${bulkForm.prefix}-A3M9, ...`}
              </p>
            </div>
          )}

          {renderDiscountFields(bulkForm, setBulkForm, 'bulk')}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)}>Cancel</Button>
          <Button onClick={() => bulkMutation.mutate()} loading={bulkMutation.isPending}
            disabled={!bulkForm.prefix || !bulkForm.quantity || (bulkForm.discountType === 'percent' ? !bulkForm.percentOff : !bulkForm.amountOff)}>
            Generate {bulkForm.quantity} Codes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedCodeId} onClose={() => setSelectedCodeId(null)} title="Promo Code Details" size="xl">
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
          </div>
        ) : detailData?.promoCode ? (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-lg font-bold">{detailData.promoCode.code}</span>
                {(() => {
                  const status = getCodeStatus(detailData.promoCode);
                  const config = { active: { variant: 'success' as const, label: 'Active' }, inactive: { variant: 'gray' as const, label: 'Inactive' }, expired: { variant: 'danger' as const, label: 'Expired' } };
                  const { variant, label } = config[status];
                  return <Badge variant={variant} dot>{label}</Badge>;
                })()}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Discount</p>
                  <p className="font-medium text-green-700">{formatDiscount(detailData.promoCode)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Duration</p>
                  <p className="font-medium">{formatDuration(detailData.promoCode)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Redemptions</p>
                  <p className="font-medium">{detailData.promoCode.times_redeemed} / {detailData.promoCode.max_redemptions || '\u221E'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{formatDate(detailData.promoCode.created_at)}</p>
                </div>
              </div>
              {detailData.promoCode.expires_at && (
                <div className="text-sm">
                  <span className="text-gray-500">Expires: </span>
                  <span className="font-medium">{formatDate(detailData.promoCode.expires_at)}</span>
                </div>
              )}
              {detailData.promoCode.restricted_plan_ids && detailData.promoCode.restricted_plan_ids.length > 0 && (
                <div className="text-sm">
                  <span className="text-gray-500">Plans: </span>
                  {detailData.promoCode.restricted_plan_ids.map((id) => plans?.find((p) => p.plan_id === id)?.name).filter(Boolean).join(', ')}
                </div>
              )}
              {detailData.promoCode.applicable_billing_cycles && detailData.promoCode.applicable_billing_cycles !== 'both' && (
                <div className="text-sm">
                  <span className="text-gray-500">Billing cycle: </span>
                  <span className="font-medium">{detailData.promoCode.applicable_billing_cycles === 'monthly' ? 'Monthly only' : 'Yearly only'}</span>
                </div>
              )}
            </div>

            {/* Editable Fields */}
            {canWrite && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Edit Details</h3>
                <div>
                  <label htmlFor="edit-max-redemptions" className="block text-sm text-gray-600 mb-1">Max Redemptions</label>
                  <input
                    id="edit-max-redemptions"
                    type="number"
                    min={detailData.promoCode.times_redeemed}
                    value={editMaxRedemptions}
                    onChange={(e) => setEditMaxRedemptions(e.target.value)}
                    placeholder="Unlimited"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                    aria-label="Edit max redemptions"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Leave empty for unlimited. Min: {detailData.promoCode.times_redeemed} (already redeemed). Updates in Stripe too.
                  </p>
                </div>
                <div>
                  <p className="block text-sm text-gray-600 mb-1">Category</p>
                  <Select
                    value={editCategoryId}
                    onChange={setEditCategoryId}
                    options={categoryFormOptions}
                    aria-label="Edit category"
                  />
                </div>
                <div>
                  <label htmlFor="edit-internal-name" className="block text-sm text-gray-600 mb-1">Internal Name</label>
                  <input
                    id="edit-internal-name"
                    type="text"
                    value={editInternalName}
                    onChange={(e) => setEditInternalName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                    aria-label="Edit internal name"
                  />
                </div>
                <div>
                  <label htmlFor="edit-promo-notes" className="block text-sm text-gray-600 mb-1">Notes</label>
                  <textarea
                    id="edit-promo-notes"
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                    aria-label="Edit promo code notes"
                  />
                </div>
                <Button size="sm" onClick={() => updateMutation.mutate()} loading={updateMutation.isPending}>
                  Save Changes
                </Button>
              </div>
            )}

            {/* Redemption History */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Redemption History</h3>
              {detailData.redemptions.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">No redemptions yet</p>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cycle</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {detailData.redemptions.map((r) => (
                        <tr key={r.id}>
                          <td className="px-4 py-2 text-sm">{r.business_name || r.stripe_customer_id || '-'}</td>
                          <td className="px-4 py-2 text-sm">{r.plan_name || '-'}</td>
                          <td className="px-4 py-2 text-sm">{r.billing_cycle || '-'}</td>
                          <td className="px-4 py-2 text-sm text-green-700">{r.discount_amount_cents ? formatCurrency(r.discount_amount_cents) : '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{formatDate(r.redeemed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}
        <ModalFooter>
          {canWrite && detailData?.promoCode && (
            <>
              {getCodeStatus(detailData.promoCode) === 'active' && (
                <Button variant="danger" size="sm" onClick={() => deactivateMutation.mutate(detailData.promoCode.id)} loading={deactivateMutation.isPending}>
                  Deactivate
                </Button>
              )}
              {getCodeStatus(detailData.promoCode) === 'inactive' && (
                <Button variant="success" size="sm" onClick={() => reactivateMutation.mutate(detailData.promoCode.id)} loading={reactivateMutation.isPending}>
                  Reactivate
                </Button>
              )}
            </>
          )}
          <Button variant="secondary" onClick={() => setSelectedCodeId(null)}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
